import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ["CIO / CTO", "VP IT / Enterprise Architecture", "AI Transformation Lead", "Digital Operations", "Other"];
const SIZES = ["500 – 2,000", "2,000 – 10,000", "10,000+", "Under 500"];
const FOCUS = ["Context Graph", "Correlation Engine", "Change Observability"];

const WHY = [
  { t: "Why now", d: "AI budgets are approved and pilots are everywhere — but without shared context and change visibility, returns stall. The enterprises that build their intelligence layer now will compound it for years." },
  { t: "Who is a fit", d: "CIOs, CTOs, enterprise architecture leaders, and AI transformation teams at medium-to-large enterprises with active AI agendas and real organizational complexity." },
  { t: "What we explore together", d: "Organizational context mapping, signal correlation across workflows, and observing operational and knowledge change — scoped to your highest-value domain first." },
];

const inputCls =
  "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-signal";

export default function Apply() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", company_size: "", message: "" });
  const [focus, setFocus] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [searchParams] = useSearchParams();
  const fromConference = ["conference", "qr", "deck", "talk"].includes((searchParams.get("src") || "").toLowerCase());

  useEffect(() => {
    document.title = "Apply as Design Partner — DotIQ";
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleFocus = (f) =>
    setFocus((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/design-partners`, { ...form, focus_areas: focus, source: searchParams.get("src") || "" });
      setDone(true);
      toast.success("Application received. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong submitting your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-[72px]" data-testid="apply-page">
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32">
          <p className="label-mono" data-testid="apply-eyebrow">Design partner program</p>
          <MaskedLines
            className="mt-8 font-display text-4xl font-medium leading-[1.05] tracking-tighter sm:text-6xl"
            lines={["Apply as a", "Design Partner."]}
          />
          <Reveal delay={0.5}>
            {fromConference && (
              <p className="mt-10 inline-block border border-signal/50 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-signal" data-testid="conference-welcome">
                Welcome from the session — great to have you here
              </p>
            )}
            <p className="mt-10 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
              A small cohort of enterprises shaping the institutional
              intelligence layer — and building their own ahead of the market.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 sm:py-28">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="flex flex-col gap-px bg-white/10 lg:col-span-5">
            {WHY.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.1} className="flex-1 bg-ink p-8 sm:p-10" data-testid={`apply-why-${i}`}>
                <span className="font-mono text-xs text-signal">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="mt-4 font-display text-xl font-medium tracking-tight">{w.t}</h2>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{w.d}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            {done ? (
              <div className="panel flex h-full flex-col items-start justify-center p-10 sm:p-16" data-testid="application-confirmation">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-signal">
                  <Check className="h-6 w-6 text-signal" />
                </span>
                <h2 className="mt-8 font-display text-3xl font-medium tracking-tight">Application received.</h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-white/60">
                  Thank you, {form.name.split(" ")[0] || "there"}. We review every
                  application personally and will reach out to {form.email || "you"} within
                  a few working days to schedule a discovery session.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="panel p-8 sm:p-12" data-testid="application-form">
                <p className="label-mono">Application</p>
                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" htmlFor="name">Full name</label>
                    <input id="name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Ada Lovelace" data-testid="form-name-input" />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" htmlFor="email">Work email</label>
                    <input id="email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="ada@enterprise.com" data-testid="form-email-input" />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" htmlFor="company">Company</label>
                    <input id="company" required value={form.company} onChange={set("company")} className={inputCls} placeholder="Enterprise Inc." data-testid="form-company-input" />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" htmlFor="role">Your role</label>
                    <select id="role" required value={form.role} onChange={set("role")} className={`${inputCls} bg-ink`} data-testid="form-role-select">
                      <option value="" disabled>Select role</option>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" htmlFor="size">Company size</label>
                    <select id="size" required value={form.company_size} onChange={set("company_size")} className={`${inputCls} bg-ink`} data-testid="form-size-select">
                      <option value="" disabled>Select size</option>
                      {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">Areas of interest</span>
                    <div className="flex flex-wrap gap-3" data-testid="form-focus-group">
                      {FOCUS.map((f) => (
                        <button
                          type="button"
                          key={f}
                          onClick={() => toggleFocus(f)}
                          data-testid={`form-focus-${f.toLowerCase().replace(/\s/g, "-")}`}
                          className={`border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                            focus.includes(f)
                              ? "border-signal bg-signal/10 text-white"
                              : "border-white/15 text-white/50 hover:border-white/40 hover:text-white"
                          }`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-white/45" htmlFor="message">
                      What are you trying to solve?
                    </label>
                    <textarea id="message" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your AI agenda, your organizational complexity, and where context or visibility is breaking down." data-testid="form-message-input" />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="form-submit-button"
                  className="group mt-10 inline-flex w-full items-center justify-center gap-3 bg-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white disabled:opacity-50 sm:w-auto"
                >
                  {submitting ? "Submitting…" : "Submit application"}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <p className="mt-6 text-xs leading-relaxed text-white/35">
                  Applications are reviewed by the founding team. Your information
                  is used solely to evaluate design partner fit.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
